import { Location, NgIf, NgFor } from '@angular/common';
import { Component, OnDestroy, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';

import { pageTransitions } from '../../animations/page-transitions';
import { HelpDocumentService } from '../../services/help-document.service';
import { HelpDocument, HelpDocumentFile, HELP_DOCUMENT_FIELD } from '../../models/help-document';
import { User } from '../../models/user';
import { UserService } from '../../services/user.service';
import { OrderBy, OrderByDirection } from '../../utils/constant';
import { LANGUAGE_CODE_OPTIONS } from '../../models/list';
import { SafeUrlPipe } from '../../pipes/safeurl.pipe';
import { LoadingSpinnerComponent } from '../utils/loading-spinner.component';
import { TranslationService } from '../../services/translation.service';
import { DbAnalyticsService } from '../../services/db-analytics.service';

@Component({
	selector: 'copo-help-data',
	templateUrl: './help-data.component.html',
	styleUrl: './help-data.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [TranslateModule, LoadingSpinnerComponent, NgIf, NgFor, SafeUrlPipe]
})
export class HelpDataComponent implements OnDestroy {
	user: User;
	helpDocuments: HelpDocument[] = [];
	helpDocumentSub: Subscription;
	isLoading = true;

	readonly languageOptions = LANGUAGE_CODE_OPTIONS;

	constructor(
		private readonly location: Location,
		private readonly helpDocumentService: HelpDocumentService,
		private readonly userService: UserService,
		private readonly translationService: TranslationService,
		private readonly dbAnalyticsService: DbAnalyticsService
	) {
		this.user = this.userService.user;
		this.helpDocumentSub = this.helpDocumentService
			.getHelpDocument(
				HELP_DOCUMENT_FIELD,
				undefined,
				0,
				1000,
				new OrderBy('sequence', OrderByDirection.ASC),
				false
			)
			.subscribe({
				next: res => {
					this.helpDocuments = this.getSortedHelpDocumentList(res.results);
					for (const helpDocument of this.helpDocuments) {
						helpDocument.files = this.getSortedHelpDocumentFileList(helpDocument.files);
					}
				},
				error: () => {
					this.isLoading = false;
				},
				complete: () => {
					this.isLoading = false;
				}
			});
	}

	getDocumentTitle(files: HelpDocumentFile[]) {
		const userFile = files.find(value =>
			value.lang === this.translationService.getLangWithRegion()) ?? files.find(value =>
			value.lang === 'en-GB');
		return userFile?.title || '';
	}

	getSortedHelpDocumentList(list: HelpDocument[]) {
		return list.sort((a, b) => this.getDocumentTitle(a.files).localeCompare(this.getDocumentTitle(b.files)));
	}

	getSortedHelpDocumentFileList(list: HelpDocumentFile[]) {
		list.sort((a, b) => this.getLang(a.lang).localeCompare(this.getLang(b.lang)));
		const englishFile = list.find(el => el.lang === 'en-GB');
		if (englishFile) {
			const foundIdx = list.findIndex(el => el.lang === 'en-GB');
			list.splice(foundIdx, 1);
			list.unshift(englishFile);
		}
		return list;
	}

	getURL(file: HelpDocumentFile, id: number) {
		return this.helpDocumentService.getURL(file, id);
	}

	trackDownload(file: HelpDocumentFile, id: number): void {
		this.dbAnalyticsService.trackAction('DOWNLOAD_HELP_PDF', {
			documentId: String(id),
			documentTitle: this.getDocumentTitle([file]),
		});
	}

	getLang(code: keyof typeof LANGUAGE_CODE_OPTIONS) {
		return this.languageOptions[code];
	}

	getBack() {
		this.location.back();
	}

	ngOnDestroy() {
		this.helpDocumentSub.unsubscribe();
	}
}
